import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { Button } from '../../shared/button/Button';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { Spinner } from '../../shared/spinner/Spinner';
import { getAllClientDatasets } from '../client-datasets/store';
import { getAllDocumentNames } from '../documents/store';
import styles from './PortfolioAnalytics.module.scss';
import { PortfolioAnalyticsTemplate } from './PortfolioAnalyticsTemplate';
import { PortfolioAnalyticsTemplateDeleteModal } from './PortfolioAnalyticsTemplateDeleteModal';
import { PortfolioAnalysisPage, PortfolioAnalysisTemplate } from './store/types';
import { getIsFetchingTemplates, getPortfolioAnalyticsTemplates, setPortfolioQuestion, fetchDocumentFieldsStarted, setSurveyIndex, setPortfolioAnalyticsTemplate, askPortfolioQuestionStarted, setSurveySummary, setPortfolioAnalysisPageView } from './store';

interface PortfolioAnalyticsTemplatesProps {
    changePageView: (view: PortfolioAnalysisPage) => void;
}

export const PortfolioAnalyticsTemplates: React.FC<PortfolioAnalyticsTemplatesProps> = ({ changePageView }) => {

    const [templateIdToDelete, setTemplateIdToDelete] = useState<number | null>(null);

    const datasets = useAppSelector(getAllClientDatasets);
    const documentNames = useAppSelector(getAllDocumentNames);
    const isFetchingTemplates = useAppSelector(getIsFetchingTemplates);

    const getSelectedDataset = useCallback((parentDatasetId: number) => datasets.find(({ datasetId }) => datasetId === parentDatasetId)?.agreementType || '', [datasets]);
    const getDocumentNames = useCallback((parentDatasetId: number, documentNameIds: number[]) => documentNames.filter(({ datasetId, documentNameId }) => datasetId === parentDatasetId && documentNameId && documentNameIds.includes(documentNameId)).map(({ documentName }) => documentName), [documentNames]);

    const dispatch = useAppDispatch();
    const templates = useAppSelector(getPortfolioAnalyticsTemplates);

    const editTemplate = useCallback((template: PortfolioAnalysisTemplate) => {
        const { metaData } = template;
        dispatch(setPortfolioQuestion(metaData));
        dispatch(fetchDocumentFieldsStarted(metaData.parentDatasetId!.toString()));
        dispatch(setSurveyIndex(2));
        changePageView(PortfolioAnalysisPage.SURVEY);
        dispatch(setPortfolioAnalyticsTemplate(template));
    }, [dispatch, changePageView]);

    const openDeleteTemplateModal = useCallback((portfolioAnalysisTemplateId: number) => setTemplateIdToDelete(portfolioAnalysisTemplateId), []);

    const askAgain = useCallback((template: PortfolioAnalysisTemplate) => {
        const { metaData } = template;
        const { parentDatasetId, question, fieldLabel, filters: { documentNameIds } } = metaData;
        const agreementType = getSelectedDataset(parentDatasetId!);
        const documentNames = getDocumentNames(parentDatasetId!, documentNameIds);
        dispatch(setPortfolioQuestion(metaData));
        dispatch(fetchDocumentFieldsStarted(metaData.parentDatasetId!.toString()));
        dispatch(askPortfolioQuestionStarted(metaData));
        dispatch(setSurveySummary({ agreementType, documentNames, fieldLabel, questionAsked: question }));
        changePageView(PortfolioAnalysisPage.RESULTS);
        dispatch(setPortfolioAnalyticsTemplate(template));
    }, [dispatch, getDocumentNames, getSelectedDataset, changePageView]);

    const getTemplateButtons = useCallback((template: PortfolioAnalysisTemplate) => (
        <div className={styles.buttonsWrapper}>
            <Button label='Delete' onClick={() => openDeleteTemplateModal(template.portfolioAnalysisTemplateId)} />
            <div className={styles.groupedButtons}>
                <Button label='Edit' onClick={() => editTemplate(template)} />
                <Button label='Ask Again' onClick={() => askAgain(template)} />
            </div>
        </div>
    ), [openDeleteTemplateModal, editTemplate, askAgain]);

    const changeView = useCallback(() => dispatch(setPortfolioAnalysisPageView(PortfolioAnalysisPage.SELECT)), [dispatch]);

    const body = useMemo(() => {
        if (isFetchingTemplates) {
            return <Spinner />;
        }
        if (templates.length === 0) {
            return <div className={styles.noTemplatesWrapper}>You do not currently have any templates, please complete the survey and click save on the results page to add a new template.</div>;
        }
        return (
            <Scrollable>
                {templates.map(template => (
                    <PortfolioAnalyticsTemplate template={template} buttons={getTemplateButtons(template)} key={template.portfolioAnalysisTemplateId} />
                ))}
            </Scrollable>
        );
    }, [isFetchingTemplates, getTemplateButtons, templates]);

    return (
        <div className={styles.templatesPageWrapper}>
            <div className={styles.templatesHeaderWrapper}>
                <div className={styles.headerTitle}>Portfolio Analytics Templates</div>
                <div className={styles.changeViewWrapper} onClick={changeView}>
                    <div className={styles.label}>← Back</div>
                </div>
            </div>
            <div className={styles.templateWrapper}>
                {body}
            </div>
            <PortfolioAnalyticsTemplateDeleteModal templateIdToDelete={templateIdToDelete} setTemplateIdToDelete={setTemplateIdToDelete} />
        </div>
    );
};
